<script setup>
import { computed, onMounted, onUnmounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter, useRoute } from 'vue-router';
import { formatTime } from '../../constants';
import Spinner from 'shared/components/Spinner.vue';
import Modal from 'dashboard/components/Modal.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import LiveReportsAPI from 'dashboard/api/liveReports';
import format from 'date-fns/format';
import {} from '../../helper/contactStagesPalette';

const props = defineProps({
  stagesTable: {
    type: Array,
    default: () => [],
  },
  cohortSummary: {
    type: Object,
    default: () => ({
      total_contacts: 0,
      total_won: 0,
      conversion_rate: 0,
      avg_won_time_seconds: 0,
    }),
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
  filterParams: {
    type: Object,
    default: () => ({}),
  },
  showUtmBreakdown: {
    type: Boolean,
    default: false,
  },
  utmSources: {
    type: Array,
    default: () => [],
  },
  utmTotals: {
    type: Object,
    default: () => ({}),
  },
  utmRevenue: {
    type: Object,
    default: () => ({}),
  },
  activeView: {
    type: String,
    default: 'table',
  },
});

const { t, locale } = useI18n();
const router = useRouter();
const route = useRoute();

const accountId = computed(() => route.params.accountId);

const hasRows = computed(() => {
  return props.stagesTable && props.stagesTable.length > 0;
});

const formatDuration = seconds => {
  if (!seconds || seconds <= 0) return '-';
  return formatTime(seconds);
};

const getStageMeta = row => {
  if (row.is_unassigned) {
    return {
      dot: 'bg-slate-400 ring-2 ring-slate-400/20',
      icon: 'i-lucide-help-circle',
      textColor: 'text-n-slate-11',
    };
  }
  if (row.is_won_stage) {
    return {
      dot: 'bg-emerald-500 ring-2 ring-emerald-500/30',
      icon: 'i-lucide-award',
      textColor: 'text-emerald-700 dark:text-emerald-300 font-bold',
    };
  }
  const lower = (row.stage || '').toLowerCase();
  if (lower.includes('perdido') || lower.includes('cancel')) {
    return {
      dot: 'bg-rose-500 ring-2 ring-rose-500/30',
      icon: 'i-lucide-x-circle',
      textColor: 'text-rose-700 dark:text-rose-300',
    };
  }
  if (lower.includes('agend')) {
    return {
      dot: 'bg-indigo-500 ring-2 ring-indigo-500/30',
      icon: 'i-lucide-calendar-check',
      textColor: 'text-indigo-700 dark:text-indigo-300',
    };
  }
  if (lower.includes('cotiz')) {
    return {
      dot: 'bg-amber-500 ring-2 ring-amber-500/30',
      icon: 'i-lucide-file-text',
      textColor: 'text-amber-700 dark:text-amber-300',
    };
  }
  if (lower.includes('contactado') && !lower.includes('no')) {
    return {
      dot: 'bg-blue-500 ring-2 ring-blue-500/30',
      icon: 'i-lucide-phone-call',
      textColor: 'text-blue-700 dark:text-blue-300',
    };
  }
  return {
    dot: 'bg-slate-400 ring-2 ring-slate-400/30',
    icon: 'i-lucide-clock',
    textColor: 'text-n-slate-12',
  };
};

// Color plano para la barra de participacion en la tabla
const getBarClass = row => {
  if (row.is_unassigned) return 'bg-n-slate-8';
  if (row.is_won_stage) return 'bg-emerald-500';
  const lower = (row.stage || '').toLowerCase();
  if (lower.includes('perdido') || lower.includes('cancel'))
    return 'bg-rose-500';
  if (lower.includes('agend')) return 'bg-indigo-500';
  if (lower.includes('cotiz')) return 'bg-amber-500';
  if (lower.includes('contactado') && !lower.includes('no'))
    return 'bg-blue-500';
  return 'bg-n-slate-9';
};

// Rejilla compartida por la cabecera, las etapas y el total del embudo:
// etapa · barra · recuento · porcentaje · tiempo en etapa.
const FUNNEL_ROW_CLASS =
  'grid items-center gap-3 px-4 py-2.5 grid-cols-[minmax(6rem,1.2fr)_minmax(0,2fr)_3rem_3rem_4.5rem]';

// html2canvas serializa el SVG a un data-URI, y ahi el CSS externo (las clases
// de Tailwind) ya no llega. Por eso los colores del SVG van como atributo fill
// resuelto en JS y no como clase: es la unica forma de que el PNG salga bien.
const isDarkTheme = ref(false);
let themeObserver = null;

const syncTheme = () => {
  isDarkTheme.value = document.body.classList.contains('dark');
};

onMounted(() => {
  syncTheme();
  themeObserver = new MutationObserver(syncTheme);
  themeObserver.observe(document.body, {
    attributes: true,
    attributeFilter: ['class'],
  });
});

onUnmounted(() => themeObserver?.disconnect());

// Modal de Contactos por Etapa
const showContactsModal = ref(false);
const isFetchingContacts = ref(false);
const activeStageRow = ref(null);
const activeUtmSource = ref(null);
const modalContacts = ref([]);
const totalModalContacts = ref(0);
const modalSearchQuery = ref('');

// --- Historial de estados por contacto (se pide al expandir la fila) ---
const expandedContactId = ref(null);
const historyByContact = ref({});
const isFetchingHistory = ref(false);
const modalHasDealAmount = ref(false);

const fetchStageContacts = async () => {
  if (!activeStageRow.value) return;
  isFetchingContacts.value = true;
  try {
    const params = {
      ...props.filterParams,
      stage: activeStageRow.value.is_total
        ? undefined
        : activeStageRow.value.stage,
      is_unassigned: activeStageRow.value.is_unassigned ? 'true' : 'false',
      utm_source: activeUtmSource.value || undefined,
      search: modalSearchQuery.value.trim() || undefined,
    };
    const response = await LiveReportsAPI.getStageContacts(params);
    modalContacts.value = response.data?.contacts || [];
    totalModalContacts.value = response.data?.total_count || 0;
    modalHasDealAmount.value = response.data?.has_deal_amount === true;
  } catch {
    modalContacts.value = [];
    totalModalContacts.value = 0;
  } finally {
    isFetchingContacts.value = false;
  }
};

const openContactsModal = async (row, utmSource = null) => {
  activeStageRow.value = row;
  activeUtmSource.value = utmSource;
  showContactsModal.value = true;
  modalSearchQuery.value = '';
  expandedContactId.value = null;
  historyByContact.value = {};
  await fetchStageContacts();
};

const getContactPercentageTooltip = row => {
  const total = props.cohortSummary.total_contacts || 0;
  return `${row.percentage}% ${t('OVERVIEW_REPORTS.CONTACT_STAGES.OF_TOTAL')} (${row.count} / ${total} ${t('OVERVIEW_REPORTS.CONTACT_STAGES.LEADS_LABEL')})`;
};

// Leads unicos de la fuente. Se prefiere el total que calcula el backend porque
// sumar la columna doble-contaria a un contacto que cambio de etapa dos veces
// dentro del periodo (modo Actividad).
const getUtmTotalCount = source => {
  const exact = props.utmTotals?.[source];
  if (typeof exact === 'number') return exact;
  if (!props.stagesTable) return 0;
  return props.stagesTable.reduce((sum, row) => {
    return sum + (row.utm_counts?.[source] || 0);
  }, 0);
};

const getUtmCellTooltip = (row, source, count) => {
  return `${count} ${t('OVERVIEW_REPORTS.CONTACT_STAGES.LEADS_LABEL')} (${source}) - ${row.stage}`;
};

// La etapa ganada es la unica fila con is_won_stage, es el numerador de la conversion
const wonRow = computed(
  () => (props.stagesTable || []).find(row => row.is_won_stage) || null
);

const getUtmWonCount = source => wonRow.value?.utm_counts?.[source] || 0;

// Conversion por fuente: ganados / total de la columna, el mismo denominador
// que muestra la fila de totales, para que el numero sea auditable a simple vista
const getUtmConversionRate = source => {
  const total = getUtmTotalCount(source);
  if (!total) return null;
  return Math.round((getUtmWonCount(source) / total) * 1000) / 10;
};

// Verde si iguala o supera la conversion global del periodo, ambar si queda
// por debajo, rojo si la fuente no convirtio nada
const getConversionToneClass = rate => {
  if (rate === null || rate === undefined) {
    return 'text-n-slate-9 border-transparent';
  }
  if (rate <= 0) {
    return 'text-rose-700 dark:text-rose-300 bg-rose-500/10 border-rose-500/25';
  }
  if (rate >= (props.cohortSummary.conversion_rate || 0)) {
    return 'text-emerald-700 dark:text-emerald-300 bg-emerald-500/10 border-emerald-500/25';
  }
  return 'text-amber-700 dark:text-amber-300 bg-amber-500/10 border-amber-500/25';
};

const getUtmConversionTooltip = source =>
  t('OVERVIEW_REPORTS.CONTACT_STAGES.CONVERSION_CELL_TOOLTIP', {
    won: getUtmWonCount(source),
    total: getUtmTotalCount(source),
    source,
  });

// No conocemos la moneda de la cuenta, asi que se formatea como numero y la
// etiqueta dice "Ingresos": preferible a inventar un simbolo equivocado.
const moneyFormatter = computed(
  () =>
    new Intl.NumberFormat(locale.value || 'es', {
      maximumFractionDigits: 2,
    })
);

const formatMoney = value => {
  const amount = Number(value);
  if (!Number.isFinite(amount) || amount === 0) return '-';
  return moneyFormatter.value.format(amount);
};

const hasDealAmount = computed(
  () => props.cohortSummary.has_deal_amount === true
);

const getUtmRevenue = source => Number(props.utmRevenue?.[source] || 0);

const hasAnyRevenue = computed(
  () =>
    hasDealAmount.value && Number(props.cohortSummary.total_revenue || 0) > 0
);

const getUtmRevenueTooltip = source =>
  t('OVERVIEW_REPORTS.CONTACT_STAGES.REVENUE_CELL_TOOLTIP', {
    amount: formatMoney(getUtmRevenue(source)),
    source,
  });

const globalConversionTooltip = computed(() =>
  t('OVERVIEW_REPORTS.CONTACT_STAGES.CONVERSION_GLOBAL_TOOLTIP', {
    won: props.cohortSummary.total_won,
    total: props.cohortSummary.total_contacts,
  })
);

// value_in_seconds se guarda como transitioned_at - contact.created_at, o sea
// antiguedad del lead al alcanzar la etapa, no permanencia en la etapa anterior
const getTimeToStageTooltip = row => {
  if (!row.avg_time_seconds || row.avg_time_seconds <= 0) {
    return t('OVERVIEW_REPORTS.CONTACT_STAGES.TIME_TO_STAGE_EMPTY');
  }
  return t('OVERVIEW_REPORTS.CONTACT_STAGES.TIME_TO_STAGE_TOOLTIP');
};

const filteredModalContacts = computed(() => {
  if (!modalSearchQuery.value.trim()) return modalContacts.value;
  const q = modalSearchQuery.value.toLowerCase().trim();
  return modalContacts.value.filter(c => {
    return (
      (c.name && c.name.toLowerCase().includes(q)) ||
      (c.phone_number && c.phone_number.toLowerCase().includes(q)) ||
      (c.email && c.email.toLowerCase().includes(q))
    );
  });
});

const navigateToContact = contactId => {
  showContactsModal.value = false;
  router.push(`/app/accounts/${accountId.value}/contacts/${contactId}`);
};

const toggleHistory = async contact => {
  if (expandedContactId.value === contact.id) {
    expandedContactId.value = null;
    return;
  }

  expandedContactId.value = contact.id;
  if (historyByContact.value[contact.id]) return;

  isFetchingHistory.value = true;
  try {
    const response = await LiveReportsAPI.getContactStageHistory(contact.id);
    historyByContact.value = {
      ...historyByContact.value,
      [contact.id]: response.data,
    };
  } catch {
    historyByContact.value = {
      ...historyByContact.value,
      [contact.id]: { events: [] },
    };
  } finally {
    isFetchingHistory.value = false;
  }
};

const historyFor = contactId => historyByContact.value[contactId] || null;

const formatDay = dateStr => {
  if (!dateStr) return '-';
  try {
    return format(new Date(dateStr), 'dd MMM yyyy');
  } catch {
    return dateStr;
  }
};

const formatDate = dateStr => {
  if (!dateStr) return '-';
  try {
    return format(new Date(dateStr), 'dd MMM yyyy, HH:mm');
  } catch {
    return dateStr;
  }
};
</script>

<template>
  <div class="flex flex-col gap-3 w-full">
    <!-- Estado de Carga -->
    <div
      v-if="isLoading"
      class="flex items-center justify-center py-12 text-n-slate-11 text-sm gap-2 bg-n-solid-1/50 rounded-lg border border-n-container"
    >
      <Spinner size="small" />
      <span>{{ t('OVERVIEW_REPORTS.CONTACT_STAGES.LOADING_MESSAGE') }}</span>
    </div>

    <!-- 1. VISTA TABLA DETALLADA (PREDETERMINADA) -->
    <div
      v-else-if="hasRows && activeView === 'table'"
      class="border border-n-container rounded-lg overflow-x-auto bg-n-solid-1 w-full"
    >
      <!-- TABLA SIMPLE (SIN DESGLOSE UTM) -->
      <table v-if="!showUtmBreakdown" class="w-full text-left border-collapse">
        <thead>
          <tr
            class="bg-n-slate-2/70 dark:bg-n-solid-2 border-b border-n-container"
          >
            <!-- Estado -->
            <th
              scope="col"
              class="py-2 px-4 text-3xs font-semibold text-n-slate-10 uppercase tracking-wider"
            >
              {{ t('OVERVIEW_REPORTS.CONTACT_STAGES.TABLE_HEADER.STAGE') }}
            </th>
            <!-- Distribucion (barra + % del total) -->
            <th
              scope="col"
              class="hidden sm:table-cell w-2/5 py-2 px-4 text-3xs font-semibold text-n-slate-10 uppercase tracking-wider"
            >
              {{
                t('OVERVIEW_REPORTS.CONTACT_STAGES.TABLE_HEADER.COHORT_PERCENT')
              }}
            </th>
            <!-- Tiempo a etapa (antiguedad promedio del lead al llegar aqui) -->
            <th
              v-tooltip.top="
                t('OVERVIEW_REPORTS.CONTACT_STAGES.TIME_TO_STAGE_TOOLTIP')
              "
              scope="col"
              class="hidden md:table-cell w-32 py-2 px-4 text-3xs font-semibold text-n-slate-10 uppercase tracking-wider text-right cursor-help"
            >
              <span class="inline-flex items-center gap-1">
                {{
                  t(
                    'OVERVIEW_REPORTS.CONTACT_STAGES.TABLE_HEADER.TIME_TO_STAGE'
                  )
                }}
                <span class="i-lucide-info size-2.5 text-n-slate-9" />
              </span>
            </th>
            <!-- Contactos -->
            <th
              scope="col"
              class="w-24 py-2 px-4 text-3xs font-semibold text-n-slate-10 uppercase tracking-wider text-right"
            >
              {{ t('OVERVIEW_REPORTS.CONTACT_STAGES.TABLE_HEADER.CONTACTS') }}
            </th>
          </tr>
        </thead>
        <tbody class="divide-y divide-n-container/40">
          <tr
            v-for="(row, index) in stagesTable"
            :key="index"
            class="transition-all group text-xs cursor-pointer select-none"
            :class="[
              row.is_won_stage
                ? 'bg-emerald-500/5 dark:bg-emerald-500/10 hover:bg-emerald-500/15'
                : 'hover:bg-n-slate-2/60 dark:hover:bg-n-solid-2/60',
              row.is_unassigned ? 'opacity-90' : '',
            ]"
            @click="openContactsModal(row)"
          >
            <!-- Estado de Contacto con Icono y Badge -->
            <td class="py-2 px-4">
              <div class="flex items-center gap-2.5 min-w-0">
                <span
                  class="size-2 rounded-full flex-shrink-0 transition-transform group-hover:scale-125"
                  :class="[
                    getStageMeta(row).dot,
                    row.is_won_stage
                      ? 'ring-2 ring-emerald-500/30'
                      : 'ring-2 ring-blue-500/20',
                  ]"
                />
                <span
                  class="truncate text-xs group-hover:text-n-blue-11 transition-colors"
                  :class="[
                    row.is_won_stage
                      ? 'font-bold text-emerald-800 dark:text-emerald-200'
                      : 'text-n-slate-12 font-medium',
                    row.count === 0 && !row.is_won_stage
                      ? 'text-n-slate-10 font-normal'
                      : '',
                    row.is_unassigned
                      ? 'italic text-n-slate-11 font-medium'
                      : '',
                  ]"
                  :title="row.stage"
                >
                  {{ row.stage }}
                </span>
                <span
                  v-if="row.is_won_stage"
                  class="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-3xs font-bold uppercase tracking-wider bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border border-emerald-500/30 flex-shrink-0 shadow-2xs"
                >
                  <span class="i-lucide-trophy size-2.5" />
                  {{ t('OVERVIEW_REPORTS.CONTACT_STAGES.WON_BADGE') }}
                </span>
                <span
                  class="i-lucide-arrow-right size-3 text-n-slate-9 opacity-0 group-hover:opacity-100 transition-opacity ml-auto flex-shrink-0"
                />
              </div>
            </td>

            <!-- Distribucion: barra proporcional + % del total -->
            <td class="hidden sm:table-cell py-2 px-4">
              <div class="flex items-center gap-2.5">
                <div
                  class="h-1.5 flex-1 rounded-full bg-n-alpha-2 overflow-hidden min-w-[40px]"
                >
                  <div
                    class="h-full rounded-full transition-all duration-500"
                    :class="getBarClass(row)"
                    :style="{ width: `${Math.min(100, row.percentage)}%` }"
                  />
                </div>
                <span
                  class="text-3xs tabular-nums w-9 text-right flex-shrink-0"
                  :class="row.count > 0 ? 'text-n-slate-11' : 'text-n-slate-9'"
                >
                  {{ row.percentage }}%
                </span>
              </div>
            </td>

            <!-- Tiempo promedio del lead hasta alcanzar esta etapa -->
            <td class="hidden md:table-cell py-2 px-4 text-right">
              <span
                v-tooltip.top="getTimeToStageTooltip(row)"
                class="text-xs tabular-nums cursor-help"
                :class="
                  row.avg_time_seconds > 0
                    ? 'text-n-slate-11'
                    : 'text-n-slate-9'
                "
              >
                {{ formatDuration(row.avg_time_seconds) }}
              </span>
            </td>

            <!-- Total Contactos con Tooltip de Porcentaje -->
            <td class="py-2 px-4 text-right">
              <span
                v-tooltip.top="getContactPercentageTooltip(row)"
                class="font-semibold text-xs tabular-nums transition-colors"
                :class="
                  row.count > 0
                    ? 'text-n-slate-12 group-hover:text-n-blue-11 font-bold'
                    : 'text-n-slate-9 font-normal'
                "
              >
                {{ row.count }}
              </span>
            </td>
          </tr>
        </tbody>

        <!-- Fila Consolidada de Totales -->
        <tfoot
          class="bg-n-slate-2/80 dark:bg-n-solid-2/95 border-t border-n-container font-semibold"
        >
          <tr class="text-xs">
            <td class="py-2 px-4">
              <div class="flex items-center gap-1.5">
                <span class="i-lucide-sigma size-3.5 text-n-slate-10" />
                <span
                  class="text-3xs font-bold uppercase tracking-wider text-n-slate-12"
                >
                  {{ t('OVERVIEW_REPORTS.CONTACT_STAGES.TOTAL_ROW') }}
                </span>
              </div>
            </td>
            <td class="hidden sm:table-cell py-2 px-4" />
            <td
              v-tooltip.top="
                t('OVERVIEW_REPORTS.CONTACT_STAGES.SUMMARY_AVG_CLOSING')
              "
              class="hidden md:table-cell py-2 px-4 text-right text-3xs tabular-nums text-n-slate-11 cursor-help"
            >
              {{ formatDuration(cohortSummary.avg_won_time_seconds) }}
            </td>
            <td class="py-2 px-4 text-right">
              <span
                v-tooltip.top="
                  `100% ${t('OVERVIEW_REPORTS.CONTACT_STAGES.OF_TOTAL')}`
                "
                class="font-bold text-xs tabular-nums text-n-slate-12 cursor-pointer"
              >
                {{ cohortSummary.total_contacts }}
              </span>
            </td>
          </tr>
        </tfoot>
      </table>

      <!-- TABLA MATRIZ CON DESGLOSE POR FUENTES UTM -->
      <table v-else class="w-full text-left border-collapse min-w-full">
        <thead>
          <tr
            class="bg-n-slate-2/70 dark:bg-n-solid-2 border-b border-n-container"
          >
            <!-- Estado -->
            <th
              scope="col"
              class="sticky ltr:left-0 rtl:right-0 z-10 bg-n-slate-2 dark:bg-n-solid-2 py-2 px-4 text-3xs font-semibold text-n-slate-10 uppercase tracking-wider min-w-[180px]"
            >
              {{ t('OVERVIEW_REPORTS.CONTACT_STAGES.TABLE_HEADER.STAGE') }}
            </th>
            <!-- Total Contactos -->
            <th
              scope="col"
              class="py-2 px-3 text-3xs font-semibold text-n-slate-10 uppercase tracking-wider text-center min-w-[80px]"
            >
              {{ t('OVERVIEW_REPORTS.CONTACT_STAGES.TABLE_HEADER.CONTACTS') }}
            </th>
            <!-- Tiempo a etapa -->
            <th
              v-tooltip.top="
                t('OVERVIEW_REPORTS.CONTACT_STAGES.TIME_TO_STAGE_TOOLTIP')
              "
              scope="col"
              class="hidden md:table-cell py-2 px-3 text-3xs font-semibold text-n-slate-10 uppercase tracking-wider text-center min-w-[104px] cursor-help"
            >
              <span class="inline-flex items-center gap-1">
                {{
                  t(
                    'OVERVIEW_REPORTS.CONTACT_STAGES.TABLE_HEADER.TIME_TO_STAGE'
                  )
                }}
                <span class="i-lucide-info size-2.5 text-n-slate-9" />
              </span>
            </th>
            <!-- Columnas Dinámicas de Fuentes UTM -->
            <th
              v-for="source in utmSources"
              :key="source"
              scope="col"
              class="py-2 px-2.5 text-3xs font-semibold text-n-blue-11 uppercase tracking-wider text-center min-w-[86px] bg-n-blue-2/60 dark:bg-n-blue-3/20 border-l border-n-container/30"
            >
              <div class="flex items-center justify-center gap-1">
                <span
                  class="i-lucide-megaphone size-2.5 flex-shrink-0 text-n-brand opacity-90"
                />
                <span class="truncate max-w-[110px]" :title="source">{{
                  source
                }}</span>
              </div>
            </th>
          </tr>
        </thead>
        <tbody class="divide-y divide-n-container/40">
          <tr
            v-for="(row, index) in stagesTable"
            :key="index"
            class="transition-all group text-xs select-none"
            :class="[
              row.is_won_stage
                ? 'bg-emerald-500/5 dark:bg-emerald-500/10 hover:bg-emerald-500/15'
                : 'hover:bg-n-slate-2/60 dark:hover:bg-n-solid-2/60',
              row.is_unassigned ? 'opacity-90' : '',
            ]"
          >
            <!-- Estado de Contacto (Clic abre modal con todos los leads del estado) -->
            <td
              class="sticky ltr:left-0 rtl:right-0 z-10 bg-n-solid-1 group-hover:bg-n-slate-2 dark:group-hover:bg-n-solid-2 py-2 px-4 cursor-pointer"
              @click="openContactsModal(row)"
            >
              <div class="flex items-center gap-2.5 min-w-0">
                <span
                  class="size-2 rounded-full flex-shrink-0 transition-transform group-hover:scale-125"
                  :class="[
                    getStageMeta(row).dot,
                    row.is_won_stage
                      ? 'ring-2 ring-emerald-500/30'
                      : 'ring-2 ring-blue-500/20',
                  ]"
                />
                <span
                  class="truncate text-xs group-hover:text-n-blue-11 transition-colors"
                  :class="[
                    row.is_won_stage
                      ? 'font-bold text-emerald-800 dark:text-emerald-200'
                      : 'text-n-slate-12 font-medium',
                    row.count === 0 && !row.is_won_stage
                      ? 'text-n-slate-10 font-normal'
                      : '',
                    row.is_unassigned
                      ? 'italic text-n-slate-11 font-medium'
                      : '',
                  ]"
                  :title="row.stage"
                >
                  {{ row.stage }}
                </span>
                <span
                  v-if="row.is_won_stage"
                  class="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-3xs font-bold uppercase tracking-wider bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border border-emerald-500/30 flex-shrink-0 shadow-2xs"
                >
                  <span class="i-lucide-trophy size-2.5" />
                  {{ t('OVERVIEW_REPORTS.CONTACT_STAGES.WON_BADGE') }}
                </span>
              </div>
            </td>

            <!-- Total Contactos con Tooltip de % -->
            <td
              class="py-2 px-3 text-center cursor-pointer"
              @click="openContactsModal(row)"
            >
              <span
                v-tooltip.top="getContactPercentageTooltip(row)"
                class="font-semibold text-xs tabular-nums transition-colors"
                :class="
                  row.count > 0
                    ? 'text-n-slate-12 group-hover:text-n-blue-11 font-bold'
                    : 'text-n-slate-9 font-normal'
                "
              >
                {{ row.count }}
              </span>
            </td>

            <!-- Tiempo promedio del lead hasta alcanzar esta etapa -->
            <td class="hidden md:table-cell py-2 px-3 text-center">
              <span
                v-tooltip.top="getTimeToStageTooltip(row)"
                class="text-xs tabular-nums cursor-help"
                :class="
                  row.avg_time_seconds > 0
                    ? 'text-n-slate-11'
                    : 'text-n-slate-9'
                "
              >
                {{ formatDuration(row.avg_time_seconds) }}
              </span>
            </td>

            <!-- Celdas Dinámicas de Fuentes UTM (Clic abre modal filtrado por ese Estado + UTM) -->
            <td
              v-for="source in utmSources"
              :key="source"
              class="py-2 px-2.5 text-center cursor-pointer transition-colors border-l border-n-container/30 hover:bg-n-blue-2/50 dark:hover:bg-n-blue-3/10"
              @click.stop="openContactsModal(row, source)"
            >
              <span
                v-if="(row.utm_counts?.[source] || 0) > 0"
                v-tooltip.top="
                  getUtmCellTooltip(row, source, row.utm_counts?.[source] || 0)
                "
                class="inline-flex items-center justify-center font-bold text-xs tabular-nums min-w-[24px] px-2 py-0.5 rounded-md transition-all text-n-blue-11 bg-n-blue-3 dark:bg-n-blue-3/30 hover:bg-n-blue-4 border border-n-blue-6/40"
              >
                {{ row.utm_counts?.[source] || 0 }}
              </span>
              <span
                v-else
                class="inline-flex items-center justify-center text-xs font-normal text-n-slate-8 opacity-25 select-none"
              >
                -
              </span>
            </td>
          </tr>
        </tbody>

        <!-- Fila Consolidada de Totales para Vista UTM -->
        <tfoot
          class="bg-n-slate-2/80 dark:bg-n-solid-2/95 border-t border-n-container font-semibold"
        >
          <tr class="text-xs">
            <td
              class="sticky ltr:left-0 rtl:right-0 z-10 bg-n-slate-2 dark:bg-n-solid-2 py-2 px-4"
            >
              <div class="flex items-center gap-1.5">
                <span class="i-lucide-sigma size-3.5 text-n-slate-10" />
                <span
                  class="text-3xs font-bold uppercase tracking-wider text-n-slate-12"
                >
                  {{ t('OVERVIEW_REPORTS.CONTACT_STAGES.TOTAL_ROW') }}
                </span>
              </div>
            </td>
            <td class="py-2 px-3 text-center">
              <span class="font-bold text-xs tabular-nums text-n-slate-12">
                {{ cohortSummary.total_contacts }}
              </span>
            </td>
            <td
              v-tooltip.top="
                t('OVERVIEW_REPORTS.CONTACT_STAGES.SUMMARY_AVG_CLOSING')
              "
              class="hidden md:table-cell py-2 px-3 text-center text-3xs tabular-nums text-n-slate-11 cursor-help"
            >
              {{ formatDuration(cohortSummary.avg_won_time_seconds) }}
            </td>
            <td
              v-for="source in utmSources"
              :key="source"
              class="py-2 px-2.5 text-center bg-n-blue-2/40 dark:bg-n-blue-3/10 border-l border-n-container/30"
            >
              <span
                v-if="getUtmTotalCount(source) > 0"
                class="inline-flex items-center justify-center font-bold text-xs tabular-nums min-w-[24px] px-2 py-0.5 rounded-md text-n-blue-11 bg-n-blue-3 dark:bg-n-blue-3/40 border border-n-blue-6/50"
              >
                {{ getUtmTotalCount(source) }}
              </span>
              <span
                v-else
                class="inline-flex items-center justify-center text-xs font-normal text-n-slate-8 opacity-25 select-none"
              >
                -
              </span>
            </td>
          </tr>

          <!-- Conversión a venta por fuente: ganados / total de cada columna -->
          <tr v-if="wonRow" class="text-xs border-t border-n-container/60">
            <td
              class="sticky ltr:left-0 rtl:right-0 z-10 bg-n-slate-2 dark:bg-n-solid-2 py-2 px-4"
            >
              <div
                v-tooltip.top="
                  t('OVERVIEW_REPORTS.CONTACT_STAGES.CONVERSION_ROW_TOOLTIP')
                "
                class="flex items-center gap-1.5 cursor-help"
              >
                <span class="i-lucide-target size-3.5 text-n-slate-10" />
                <span
                  class="text-3xs font-bold uppercase tracking-wider text-n-slate-12"
                >
                  {{ t('OVERVIEW_REPORTS.CONTACT_STAGES.CONVERSION_ROW') }}
                </span>
                <span class="i-lucide-info size-2.5 text-n-slate-9" />
              </div>
            </td>
            <td class="py-2 px-3 text-center">
              <span
                v-tooltip.top="globalConversionTooltip"
                class="inline-flex items-center justify-center font-bold text-xs tabular-nums min-w-[24px] px-2 py-0.5 rounded-md border cursor-help"
                :class="getConversionToneClass(cohortSummary.conversion_rate)"
              >
                {{ cohortSummary.conversion_rate }}%
              </span>
            </td>
            <td class="hidden md:table-cell py-2 px-3" />
            <td
              v-for="source in utmSources"
              :key="source"
              class="py-2 px-2.5 text-center bg-n-blue-2/40 dark:bg-n-blue-3/10 border-l border-n-container/30"
            >
              <span
                v-if="getUtmConversionRate(source) !== null"
                v-tooltip.top="getUtmConversionTooltip(source)"
                class="inline-flex items-center justify-center font-bold text-xs tabular-nums min-w-[24px] px-2 py-0.5 rounded-md border cursor-help"
                :class="getConversionToneClass(getUtmConversionRate(source))"
              >
                {{ getUtmConversionRate(source) }}%
              </span>
              <span
                v-else
                class="inline-flex items-center justify-center text-xs font-normal text-n-slate-8 opacity-25 select-none"
              >
                -
              </span>
            </td>
          </tr>

          <!-- Ingresos por fuente: el numerador del ROAS -->
          <tr
            v-if="hasDealAmount"
            class="text-xs border-t border-n-container/60"
          >
            <td
              class="sticky ltr:left-0 rtl:right-0 z-10 bg-n-slate-2 dark:bg-n-solid-2 py-2 px-4"
            >
              <div
                v-tooltip.top="
                  t('OVERVIEW_REPORTS.CONTACT_STAGES.REVENUE_ROW_TOOLTIP')
                "
                class="flex items-center gap-1.5 cursor-help"
              >
                <span class="i-lucide-banknote size-3.5 text-n-slate-10" />
                <span
                  class="text-3xs font-bold uppercase tracking-wider text-n-slate-12"
                >
                  {{ t('OVERVIEW_REPORTS.CONTACT_STAGES.REVENUE_ROW') }}
                </span>
                <span class="i-lucide-info size-2.5 text-n-slate-9" />
              </div>
            </td>
            <td class="py-2 px-3 text-center">
              <span
                class="font-bold text-xs tabular-nums"
                :class="
                  hasAnyRevenue
                    ? 'text-emerald-700 dark:text-emerald-300'
                    : 'text-n-slate-9'
                "
              >
                {{ formatMoney(cohortSummary.total_revenue) }}
              </span>
            </td>
            <td class="hidden md:table-cell py-2 px-3" />
            <td
              v-for="source in utmSources"
              :key="source"
              class="py-2 px-2.5 text-center bg-n-blue-2/40 dark:bg-n-blue-3/10 border-l border-n-container/30"
            >
              <span
                v-if="getUtmRevenue(source) > 0"
                v-tooltip.top="getUtmRevenueTooltip(source)"
                class="inline-flex items-center justify-center font-bold text-xs tabular-nums px-2 py-0.5 rounded-md border cursor-help text-emerald-700 dark:text-emerald-300 bg-emerald-500/10 border-emerald-500/25"
              >
                {{ formatMoney(getUtmRevenue(source)) }}
              </span>
              <span
                v-else
                class="inline-flex items-center justify-center text-xs font-normal text-n-slate-8 opacity-25 select-none"
              >
                -
              </span>
            </td>
          </tr>
        </tfoot>
      </table>
    </div>

    <!-- 2. VISTA EMBUDO: una fila por etapa con su barra, el recuento, el
         porcentaje y el tiempo en etapa, y el total de conversión al pie.
         Antes era una silueta en SVG: bonita, pero costaba leer las cifras
         y no es lo que pide el diseño. -->
    <div
      v-else-if="hasRows && activeView === 'funnel'"
      class="w-full overflow-hidden border rounded-lg border-n-container bg-n-solid-1"
    >
      <div :class="FUNNEL_ROW_CLASS" class="border-b border-n-container">
        <span
          class="text-[10.5px] font-semibold tracking-[0.09em] uppercase text-n-slate-10"
        >
          {{ t('OVERVIEW_REPORTS.CONTACT_STAGES.FUNNEL_STAGE') }}
        </span>
        <span />
        <span
          class="text-[10.5px] font-semibold tracking-[0.09em] uppercase text-right text-n-slate-10"
        >
          {{ t('OVERVIEW_REPORTS.CONTACT_STAGES.FUNNEL_COUNT') }}
        </span>
        <span
          class="text-[10.5px] font-semibold tracking-[0.09em] uppercase text-right text-n-slate-10"
        >
          %
        </span>
        <span
          class="text-[10.5px] font-semibold tracking-[0.09em] uppercase text-right text-n-slate-10"
        >
          {{ t('OVERVIEW_REPORTS.CONTACT_STAGES.FUNNEL_IN_STAGE') }}
        </span>
      </div>

      <button
        v-for="row in stagesTable"
        :key="row.stage"
        type="button"
        :class="FUNNEL_ROW_CLASS"
        class="w-full text-left transition-colors border-b border-n-weak last:border-b-0 hover:bg-n-alpha-1"
        @click="openContactsModal(row)"
      >
        <span class="flex items-center gap-2 min-w-0">
          <span
            class="rounded-full size-2 flex-shrink-0"
            :class="getStageMeta(row).dot"
          />
          <span
            class="text-[13px] truncate"
            :class="row.count > 0 ? 'text-n-slate-12' : 'text-n-slate-10'"
            :title="row.stage"
          >
            {{ row.stage }}
          </span>
        </span>

        <span class="h-2 overflow-hidden rounded-full bg-n-alpha-2">
          <span
            class="block h-full rounded-full"
            :class="getBarClass(row)"
            :style="{ width: `${Math.min(100, row.percentage)}%` }"
          />
        </span>

        <span
          class="text-[13px] font-semibold text-right tabular-nums"
          :class="row.count > 0 ? 'text-n-slate-12' : 'text-n-slate-9'"
        >
          {{ row.count }}
        </span>
        <span class="text-xs text-right tabular-nums text-n-slate-11">
          {{ row.percentage }}%
        </span>
        <span class="text-xs text-right tabular-nums text-n-slate-11">
          {{ formatDuration(row.avg_time_seconds) }}
        </span>
      </button>

      <div
        :class="FUNNEL_ROW_CLASS"
        class="border-t border-n-strong bg-n-alpha-1/60"
      >
        <span class="text-[13px] font-semibold text-n-slate-12">
          {{ t('OVERVIEW_REPORTS.CONTACT_STAGES.CONVERSION_ROW') }}
        </span>
        <span />
        <span
          class="text-[13px] font-semibold text-right tabular-nums text-n-slate-12"
        >
          {{ cohortSummary.total_won }}
        </span>
        <span
          class="text-[13px] font-semibold text-right tabular-nums text-n-teal-11"
        >
          {{ cohortSummary.conversion_rate }}%
        </span>
        <span class="text-xs text-right tabular-nums text-n-slate-11">
          {{ formatDuration(cohortSummary.avg_won_time_seconds) }}
        </span>
      </div>
    </div>

    <!-- Estado Vacío -->
    <div
      v-else
      class="flex flex-col items-center justify-center py-8 px-4 text-center border border-dashed border-n-container rounded-lg bg-n-solid-1"
    >
      <div
        class="size-10 rounded-full bg-n-alpha-2 flex items-center justify-center mb-2"
      >
        <span class="i-lucide-users text-n-slate-9 size-5" />
      </div>
      <p class="text-xs font-semibold text-n-slate-12 mb-0.5">
        {{ t('OVERVIEW_REPORTS.CONTACT_STAGES.NO_DATA') }}
      </p>
    </div>

    <!-- Modal de contactos de la etapa, con historial de estados por contacto -->
    <Modal
      v-if="showContactsModal"
      :show="showContactsModal"
      :on-close="() => (showContactsModal = false)"
    >
      <div class="flex flex-col max-h-[82vh] w-full p-5">
        <!-- Cabecera -->
        <div
          class="flex items-start justify-between gap-3 pb-3 border-b border-n-container"
        >
          <div class="flex items-center gap-2.5 min-w-0">
            <div
              class="size-9 rounded-lg flex items-center justify-center bg-n-blue-3 text-n-blue-11 flex-shrink-0"
            >
              <span class="i-lucide-users size-5" />
            </div>
            <div class="flex flex-col min-w-0">
              <h3
                class="text-base font-semibold text-n-slate-12 mb-0 leading-tight truncate"
              >
                {{
                  activeStageRow?.stage ||
                  t('OVERVIEW_REPORTS.CONTACT_STAGES.DRAWER_TITLE')
                }}
              </h3>
              <div class="flex items-center gap-2 mt-1 flex-wrap">
                <span class="text-3xs text-n-slate-10">
                  {{
                    t('OVERVIEW_REPORTS.CONTACT_STAGES.DRAWER_SHOWING', {
                      shown: filteredModalContacts.length,
                      total: totalModalContacts,
                    })
                  }}
                </span>
                <span
                  v-if="activeUtmSource"
                  class="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-3xs font-medium bg-n-blue-3 text-n-blue-11"
                >
                  <span class="i-lucide-megaphone size-2.5" />
                  {{ activeUtmSource }}
                </span>
              </div>
            </div>
          </div>
        </div>

        <!-- Buscador -->
        <div class="pt-3 pb-2">
          <div class="relative w-full">
            <span
              class="i-lucide-search size-4 absolute ltr:left-3 rtl:right-3 top-1/2 -translate-y-1/2 text-n-slate-10 pointer-events-none"
            />
            <input
              v-model="modalSearchQuery"
              type="text"
              class="w-full ltr:pl-9 rtl:pr-9 ltr:pr-3 rtl:pl-3 py-2 !mb-0 rounded-lg border border-n-container bg-n-solid-2 text-xs text-n-slate-12 placeholder:text-n-slate-9 focus:border-n-blue-9 focus:ring-1 focus:ring-n-blue-9 transition-colors"
              :placeholder="
                t('OVERVIEW_REPORTS.CONTACT_STAGES.DRAWER_SEARCH_PLACEHOLDER')
              "
            />
          </div>
        </div>

        <!-- Lista -->
        <div class="flex-1 overflow-y-auto min-h-[240px] max-h-[440px] py-1">
          <div
            v-if="isFetchingContacts"
            class="flex flex-col items-center justify-center py-16 gap-2 text-n-slate-11 text-xs"
          >
            <Spinner size="small" />
            <span>
              {{ t('OVERVIEW_REPORTS.CONTACT_STAGES.LOADING_MESSAGE') }}
            </span>
          </div>

          <div
            v-else-if="!filteredModalContacts.length"
            class="flex flex-col items-center justify-center py-16 text-center text-n-slate-11 gap-2"
          >
            <span class="i-lucide-user-x size-8 text-n-slate-8" />
            <span class="text-xs font-medium">
              {{ t('OVERVIEW_REPORTS.CONTACT_STAGES.DRAWER_NO_CONTACTS') }}
            </span>
          </div>

          <div v-else class="flex flex-col gap-1.5">
            <div
              v-for="contact in filteredModalContacts"
              :key="contact.id"
              class="rounded-lg border transition-colors"
              :class="
                expandedContactId === contact.id
                  ? 'border-n-blue-7 bg-n-blue-2/40 dark:bg-n-blue-3/10'
                  : 'border-n-container bg-n-solid-1 hover:border-n-slate-6'
              "
            >
              <!-- Fila del contacto: el clic despliega su historial -->
              <div class="flex items-center gap-2.5 p-2.5">
                <button
                  type="button"
                  class="flex items-center gap-2.5 min-w-0 flex-1 text-left cursor-pointer"
                  @click="toggleHistory(contact)"
                >
                  <div
                    class="size-8 rounded-full bg-n-blue-3 flex items-center justify-center text-xs font-bold text-n-blue-11 flex-shrink-0 uppercase"
                  >
                    {{ (contact.name || 'U')[0] }}
                  </div>
                  <div class="flex flex-col min-w-0 gap-0.5">
                    <span
                      class="text-xs font-semibold text-n-slate-12 truncate"
                    >
                      {{ contact.name }}
                    </span>
                    <div
                      class="flex items-center gap-x-2 gap-y-0.5 text-3xs text-n-slate-10 flex-wrap"
                    >
                      <span
                        v-if="contact.phone_number"
                        class="inline-flex items-center gap-1"
                      >
                        <span class="i-lucide-phone size-2.5" />
                        {{ contact.phone_number }}
                      </span>
                      <span
                        v-if="contact.email"
                        class="inline-flex items-center gap-1 truncate max-w-[180px]"
                      >
                        <span class="i-lucide-mail size-2.5 flex-shrink-0" />
                        {{ contact.email }}
                      </span>
                      <span class="inline-flex items-center gap-1">
                        <span class="i-lucide-calendar size-2.5" />
                        {{ formatDay(contact.created_at) }}
                      </span>
                      <span
                        class="inline-flex items-center gap-1 px-1.5 rounded-md bg-n-amber-3 text-n-amber-11"
                      >
                        <span class="i-lucide-megaphone size-2.5" />
                        {{ contact.utm_source }}
                      </span>
                    </div>
                    <!-- De donde vino: responde "3d, pero desde que etapa?" -->
                    <div
                      v-if="contact.stage_from"
                      class="flex items-center gap-1.5 text-3xs text-n-slate-11"
                    >
                      <span class="i-lucide-corner-down-right size-2.5" />
                      <span>
                        {{
                          t(
                            'OVERVIEW_REPORTS.CONTACT_STAGES.DRAWER_CAME_FROM',
                            {
                              stage: contact.stage_from,
                            }
                          )
                        }}
                      </span>
                      <span
                        v-if="contact.seconds_in_previous_stage > 0"
                        class="text-n-slate-9"
                      >
                        ·
                        {{
                          t(
                            'OVERVIEW_REPORTS.CONTACT_STAGES.DRAWER_IN_PREVIOUS_STAGE',
                            {
                              time: formatDuration(
                                contact.seconds_in_previous_stage
                              ),
                            }
                          )
                        }}
                      </span>
                    </div>
                    <div
                      v-else
                      class="flex items-center gap-1.5 text-3xs text-n-slate-9"
                    >
                      <span class="i-lucide-minus size-2.5" />
                      {{ t('OVERVIEW_REPORTS.CONTACT_STAGES.DRAWER_NO_MOVES') }}
                    </div>
                  </div>
                </button>

                <!-- Monto y acciones -->
                <div class="flex items-center gap-1.5 flex-shrink-0">
                  <div
                    v-if="modalHasDealAmount && contact.amount"
                    class="flex flex-col items-end"
                  >
                    <span
                      class="text-3xs text-n-slate-10 uppercase tracking-wide"
                    >
                      {{ t('OVERVIEW_REPORTS.CONTACT_STAGES.DRAWER_AMOUNT') }}
                    </span>
                    <span
                      class="text-xs font-bold tabular-nums text-emerald-700 dark:text-emerald-300"
                    >
                      {{ formatMoney(contact.amount) }}
                    </span>
                  </div>
                  <button
                    type="button"
                    class="size-7 rounded-md flex items-center justify-center text-n-slate-10 hover:bg-n-alpha-2 hover:text-n-slate-12 transition-colors cursor-pointer"
                    :title="
                      t('OVERVIEW_REPORTS.CONTACT_STAGES.DRAWER_VIEW_CONTACT')
                    "
                    @click.stop="navigateToContact(contact.id)"
                  >
                    <span class="i-lucide-external-link size-3.5" />
                  </button>
                  <button
                    type="button"
                    class="size-7 rounded-md flex items-center justify-center text-n-slate-10 hover:bg-n-alpha-2 hover:text-n-slate-12 transition-colors cursor-pointer"
                    :title="t('OVERVIEW_REPORTS.CONTACT_STAGES.HISTORY_TITLE')"
                    @click="toggleHistory(contact)"
                  >
                    <span
                      class="i-lucide-chevron-down size-4 transition-transform"
                      :class="
                        expandedContactId === contact.id ? 'rotate-180' : ''
                      "
                    />
                  </button>
                </div>
              </div>

              <!-- Historial de estados del contacto -->
              <div
                v-if="expandedContactId === contact.id"
                class="border-t border-n-container px-3 py-2.5"
              >
                <div
                  v-if="isFetchingHistory && !historyFor(contact.id)"
                  class="flex items-center gap-2 py-3 text-3xs text-n-slate-11"
                >
                  <Spinner size="small" />
                  <span>
                    {{ t('OVERVIEW_REPORTS.CONTACT_STAGES.HISTORY_LOADING') }}
                  </span>
                </div>

                <template v-else>
                  <p
                    class="text-3xs font-bold uppercase tracking-wide text-n-slate-10 mb-2"
                  >
                    {{ t('OVERVIEW_REPORTS.CONTACT_STAGES.HISTORY_TITLE') }}
                  </p>

                  <ol
                    class="flex flex-col ltr:border-l rtl:border-r border-n-container ltr:ml-1 rtl:mr-1"
                  >
                    <!-- Punto de partida: la creacion del lead -->
                    <li
                      class="relative ltr:pl-4 rtl:pr-4 pb-2 text-3xs text-n-slate-11"
                    >
                      <span
                        class="absolute ltr:-left-1 rtl:-right-1 top-1 size-2 rounded-full bg-n-slate-8"
                      />
                      <span class="font-semibold text-n-slate-12">
                        {{
                          t('OVERVIEW_REPORTS.CONTACT_STAGES.HISTORY_CREATED')
                        }}
                      </span>
                      <span class="text-n-slate-10">
                        · {{ formatDate(historyFor(contact.id)?.created_at) }}
                      </span>
                    </li>

                    <li
                      v-for="event in historyFor(contact.id)?.events || []"
                      :key="event.id"
                      class="relative ltr:pl-4 rtl:pr-4 pb-2 text-3xs"
                    >
                      <span
                        class="absolute ltr:-left-1 rtl:-right-1 top-1 size-2 rounded-full"
                        :class="
                          event.is_won_stage ? 'bg-emerald-500' : 'bg-n-blue-9'
                        "
                      />
                      <div class="flex items-center gap-1.5 flex-wrap">
                        <span v-if="event.from" class="text-n-slate-10">
                          {{ event.from }}
                        </span>
                        <span v-else class="text-n-slate-9 italic">
                          {{
                            t(
                              'OVERVIEW_REPORTS.CONTACT_STAGES.HISTORY_INITIAL_STAGE'
                            )
                          }}
                        </span>
                        <span
                          class="i-lucide-arrow-right size-2.5 text-n-slate-9"
                        />
                        <span
                          class="font-semibold"
                          :class="
                            event.is_won_stage
                              ? 'text-emerald-700 dark:text-emerald-300'
                              : 'text-n-slate-12'
                          "
                        >
                          {{ event.to }}
                        </span>
                        <span
                          v-if="event.seconds_in_previous_stage > 0"
                          class="px-1.5 rounded-md bg-n-alpha-2 text-n-slate-11 tabular-nums"
                        >
                          {{ formatDuration(event.seconds_in_previous_stage) }}
                        </span>
                        <span
                          v-if="event.amount"
                          class="px-1.5 rounded-md bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 font-semibold tabular-nums"
                        >
                          {{ formatMoney(event.amount) }}
                        </span>
                      </div>
                      <div class="text-n-slate-10 mt-0.5">
                        {{ formatDate(event.transitioned_at) }} ·
                        {{
                          t('OVERVIEW_REPORTS.CONTACT_STAGES.HISTORY_BY', {
                            user:
                              event.user_name ||
                              t(
                                'OVERVIEW_REPORTS.CONTACT_STAGES.HISTORY_UNKNOWN_USER'
                              ),
                          })
                        }}
                      </div>
                    </li>
                  </ol>

                  <p
                    v-if="!(historyFor(contact.id)?.events || []).length"
                    class="text-3xs text-n-slate-10 mb-0 ltr:pl-4 rtl:pr-4"
                  >
                    {{ t('OVERVIEW_REPORTS.CONTACT_STAGES.HISTORY_EMPTY') }}
                  </p>
                </template>
              </div>
            </div>
          </div>
        </div>

        <!-- Pie -->
        <div class="pt-3 border-t border-n-container flex justify-end">
          <Button
            sm
            slate
            faded
            :label="t('OVERVIEW_REPORTS.CONTACT_STAGES.CLOSE')"
            @click="showContactsModal = false"
          />
        </div>
      </div>
    </Modal>
  </div>
</template>
